import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, AlertCircle, CheckCircle, Clock, Filter, ChevronDown, ChevronUp, User, Mail, Phone, Calendar } from 'lucide-react';

// Use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Complaint {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
  };
  userType: 'customer' | 'provider';
  bookingId?: {
    _id?: string;
    bookingId?: number;
  } | string;
  subject: string;
  description: string;
  isUrgent: boolean;
  status: 'pending' | 'under_review' | 'pending_manager' | 'resolved';
  resolution?: string;
  resolvedBy?: {
    name: string;
    email: string;
  };
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ComplaintStats {
  total: number;
  urgent: number;
  pending: number;
  underReview: number;
  pendingManager: number;
  resolved: number;
}

const AdminComplaintsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [stats, setStats] = useState<ComplaintStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [resolution, setResolution] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [expandedComplaint, setExpandedComplaint] = useState<string | null>(null);
  const highlightedComplaintRef = useRef<HTMLDivElement>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [urgentFilter, setUrgentFilter] = useState<string>('');
  const [userTypeFilter, setUserTypeFilter] = useState<string>('');

  useEffect(() => {
    fetchComplaints();
    fetchStats();
  }, [statusFilter, urgentFilter, userTypeFilter]);

  // Handle highlight parameter from notifications
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (highlight === 'latest' && complaints.length > 0) {
      // Auto-expand the first (most recent) complaint
      const latestComplaint = complaints[0];
      setExpandedComplaint(latestComplaint._id);
      
      // Scroll to it after a short delay to ensure rendering
      setTimeout(() => {
        if (highlightedComplaintRef.current) {
          highlightedComplaintRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);
      
      // Remove the highlight parameter from URL
      setSearchParams({});
    }
  }, [complaints, searchParams, setSearchParams]);

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (urgentFilter) params.append('isUrgent', urgentFilter);
      if (userTypeFilter) params.append('userType', userTypeFilter);
      
      const response = await axios.get(
        `${API_BASE_URL}/api/complaints?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setComplaints(response.data.data.complaints);
      setError('');
    } catch (err: any) {
      console.error('Error fetching complaints:', err);
      setError(err.response?.data?.message || 'Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/complaints/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleUpdateStatus = async (complaintId: string, newStatus: string) => {
    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem('token');
      
      await axios.patch(
        `${API_BASE_URL}/api/complaints/${complaintId}/status`,
        {
          status: newStatus,
          resolution: newStatus === 'resolved' ? resolution : undefined
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh data
      await fetchComplaints();
      await fetchStats();
      
      setSelectedComplaint(null);
      setResolution('');
    } catch (err: any) {
      console.error('Error updating status:', err);
      alert(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'under_review':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending_manager':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'under_review':
        return <AlertCircle className="h-4 w-4" />;
      case 'pending_manager':
        return <AlertTriangle className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  if (loading && complaints.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 md:py-8">
        {/* Header */}
        <div className="mb-4 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Complaint Management</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">Review and resolve customer and provider complaints</p>
        </div>

        {/* Statistics - Responsive Grid */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-4 md:mb-8">
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 border-l-4 border-gray-500">
              <p className="text-xs md:text-sm text-gray-600">Total</p>
              <p className="text-xl md:text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 border-l-4 border-red-500">
              <p className="text-xs md:text-sm text-gray-600">Urgent</p>
              <p className="text-xl md:text-2xl font-bold text-red-600">{stats.urgent}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 border-l-4 border-yellow-500">
              <p className="text-xs md:text-sm text-gray-600">Pending</p>
              <p className="text-xl md:text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 border-l-4 border-blue-500">
              <p className="text-xs md:text-sm text-gray-600">Review</p>
              <p className="text-xl md:text-2xl font-bold text-blue-600">{stats.underReview}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 border-l-4 border-purple-500">
              <p className="text-xs md:text-sm text-gray-600">Manager</p>
              <p className="text-xl md:text-2xl font-bold text-purple-600">{stats.pendingManager}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 border-l-4 border-green-500">
              <p className="text-xs md:text-sm text-gray-600">Resolved</p>
              <p className="text-xl md:text-2xl font-bold text-green-600">{stats.resolved}</p>
            </div>
          </div>
        )}

        {/* Filters - Mobile Responsive */}
        <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center">
              <Filter className="h-4 w-4 md:h-5 md:w-5 text-gray-500 mr-2" />
              <span className="text-xs md:text-sm font-medium text-gray-700">Filters:</span>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 flex-1">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-xs md:text-sm w-full"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="pending_manager">Pending Manager</option>
                <option value="resolved">Resolved</option>
              </select>

              <select
                value={urgentFilter}
                onChange={(e) => setUrgentFilter(e.target.value)}
                className="px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-xs md:text-sm w-full"
              >
                <option value="">All Priorities</option>
                <option value="true">Urgent Only</option>
                <option value="false">Non-Urgent</option>
              </select>

              <select
                value={userTypeFilter}
                onChange={(e) => setUserTypeFilter(e.target.value)}
                className="px-2 md:px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-xs md:text-sm w-full"
              >
                <option value="">All Users</option>
                <option value="customer">Customers</option>
                <option value="provider">Providers</option>
              </select>
            </div>

            {(statusFilter || urgentFilter || userTypeFilter) && (
              <button
                onClick={() => {
                  setStatusFilter('');
                  setUrgentFilter('');
                  setUserTypeFilter('');
                }}
                className="text-xs md:text-sm text-black hover:text-gray-800 font-medium self-start sm:self-center"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Complaints List - Mobile Responsive */}
        {complaints.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-6 md:p-12 text-center">
            <AlertCircle className="h-10 md:h-12 w-10 md:w-12 text-gray-400 mx-auto mb-3 md:mb-4" />
            <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">No Complaints Found</h3>
            <p className="text-sm md:text-base text-gray-600">
              {statusFilter || urgentFilter || userTypeFilter
                ? 'No complaints match the selected filters.'
                : 'No complaints have been submitted yet.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {complaints.map((complaint, index) => (
              <div
                key={complaint._id}
                ref={index === 0 ? highlightedComplaintRef : null}
                className={`bg-white rounded-lg shadow-sm overflow-hidden transition-all ${
                  complaint.isUrgent ? 'border-2 border-red-500' : 'border border-gray-200'
                } ${expandedComplaint === complaint._id ? 'ring-2 ring-gray-500' : ''}`}
              >
                {/* Header - Mobile Responsive */}
                <div className="p-3 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3 md:mb-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {complaint.isUrgent && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded-full flex items-center animate-pulse">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            URGENT
                          </span>
                        )}
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full capitalize">
                          {complaint.userType}
                        </span>
                      </div>
                      <h3 className="text-base md:text-lg font-bold text-gray-900 break-words">{complaint.subject}</h3>
                      <p className="text-xs md:text-sm text-gray-500 mt-1">
                        ID: {complaint._id.slice(-8).toUpperCase()}
                      </p>
                      <p className="text-xs md:text-sm text-gray-500">
                        Submitted {new Date(complaint.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className={`px-2 md:px-3 py-1 rounded-lg border-2 flex items-center space-x-1 md:space-x-2 ${getStatusColor(complaint.status)} self-start`}>
                      {getStatusIcon(complaint.status)}
                      <span className="font-semibold text-xs md:text-sm whitespace-nowrap">{formatStatus(complaint.status)}</span>
                    </div>
                  </div>

                  {/* User Info - Mobile Responsive */}
                  <div className="bg-gray-50 rounded-lg p-3 md:p-4 mb-3 md:mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 text-xs md:text-sm">
                      <div className="flex items-center">
                        <User className="h-3 w-3 md:h-4 md:w-4 text-gray-500 mr-2 flex-shrink-0" />
                        <span className="font-medium truncate">{complaint.userId.name}</span>
                      </div>
                      <div className="flex items-center">
                        <Mail className="h-3 w-3 md:h-4 md:w-4 text-gray-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-600 truncate">{complaint.userId.email}</span>
                      </div>
                      {complaint.userId.phone && (
                        <div className="flex items-center">
                          <Phone className="h-3 w-3 md:h-4 md:w-4 text-gray-500 mr-2 flex-shrink-0" />
                          <span className="text-gray-600">{complaint.userId.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Related Booking Info - Mobile Responsive */}
                  {complaint.bookingId && (
                    <div className="bg-white rounded-lg p-3 md:p-4 mb-3 md:mb-4 border-l-4 border-gray-400">
                      <div className="flex flex-col sm:flex-row sm:items-center text-xs md:text-sm">
                        <Calendar className="h-4 w-4 text-black mr-2 flex-shrink-0" />
                        <span className="font-semibold text-black mr-2">Related Booking:</span>
                        <span className="text-gray-800 break-all">
                          ID: {(complaint.bookingId as any)?.bookingId || (complaint.bookingId as any)?._id?.slice(-8) || 'N/A'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Description (Expandable) */}
                  <div>
                    <button
                      onClick={() => setExpandedComplaint(expandedComplaint === complaint._id ? null : complaint._id)}
                      className="flex items-center text-sm font-medium text-black hover:text-gray-800 mb-2"
                    >
                      {expandedComplaint === complaint._id ? (
                        <>
                          <ChevronUp className="h-4 w-4 mr-1" />
                          Hide Description
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-1" />
                          Show Description
                        </>
                      )}
                    </button>
                    
                    {expandedComplaint === complaint._id && (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{complaint.description}</p>
                      </div>
                    )}
                  </div>

                  {/* Resolution */}
                  {complaint.status === 'resolved' && complaint.resolution && (
                    <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                      <p className="text-sm font-semibold text-green-900 mb-1">Resolution:</p>
                      <p className="text-sm text-gray-700">{complaint.resolution}</p>
                      {complaint.resolvedBy && (
                        <p className="text-xs text-gray-600 mt-2">
                          Resolved by {complaint.resolvedBy.name} on {new Date(complaint.resolvedAt!).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  {complaint.status !== 'resolved' && (
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                      {complaint.status === 'pending' && (
                        <button
                          onClick={() => handleUpdateStatus(complaint._id, 'under_review')}
                          disabled={updatingStatus}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                        >
                          Start Review
                        </button>
                      )}
                      
                      {(complaint.status === 'pending' || complaint.status === 'under_review') && (
                        <button
                          onClick={() => handleUpdateStatus(complaint._id, 'pending_manager')}
                          disabled={updatingStatus}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium disabled:opacity-50"
                        >
                          Escalate to Manager
                        </button>
                      )}
                      
                      <button
                        onClick={() => setSelectedComplaint(complaint)}
                        disabled={updatingStatus}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                      >
                        Mark as Resolved
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resolution Modal */}
      {selectedComplaint && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSelectedComplaint(null)}></div>
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Resolve Complaint</h3>
              <p className="text-sm text-gray-600 mb-4">Subject: {selectedComplaint.subject}</p>
              
              <textarea
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="Describe how this complaint was resolved..."
                maxLength={1000}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 mb-4"
              />
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setSelectedComplaint(null);
                    setResolution('');
                  }}
                  disabled={updatingStatus}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedComplaint._id, 'resolved')}
                  disabled={updatingStatus || !resolution.trim()}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                >
                  {updatingStatus ? 'Resolving...' : 'Confirm Resolution'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminComplaintsPage;

